// frontend/src/portal/pages/PortalInviteUsers.jsx
import { useMemo, useState, useEffect } from "react";
import { createPortalInvitations, linkTenantOrganization, getPortalInvitations, resendInvitation, revokeInvitation } from "../../api.js";
import { usePortalAuth } from "../components/RequireClient.jsx";
import ErrorBoundary from "../../components/ErrorBoundary.jsx";
import { isTenantLinkedToClerk } from "../../utils/tenantLinkage.js";

function formatSeatLabel(seatUsage, fallbackSeats) {
  if (!seatUsage && fallbackSeats == null) {
    return "Seats: --";
  }
  const total = seatUsage?.total ?? (Number.isFinite(fallbackSeats) ? fallbackSeats : null);
  const used = seatUsage?.used ?? null;
  if (total === null) {
    return `Seats used: ${used ?? "--"} / Unlimited`;
  }
  return `Seats used: ${used ?? "--"} / ${total}`;
}

function computeRemaining(seatUsage, fallbackSeats) {
  if (seatUsage?.remaining != null) return seatUsage.remaining;
  if (seatUsage?.total != null && seatUsage.used != null) {
    return Math.max(seatUsage.total - seatUsage.used, 0);
  }
  if (fallbackSeats != null) return null;
  return null;
}

function parseEmails(input) {
  if (!input) return [];
  return input
    .split(/[\s,;]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function formatDate(dateString) {
  if (!dateString) return "--";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString();
}

function getStatusBadgeStyle(status) {
  const base = {
    display: "inline-block",
    padding: "4px 10px",
    borderRadius: "var(--radius-sm)",
    fontSize: "12px",
    fontWeight: "600",
  };

  switch (status) {
    case "accepted":
      return { ...base, backgroundColor: "var(--success-bg)", color: "var(--success)" };
    case "pending":
      return { ...base, backgroundColor: "var(--warning-bg)", color: "var(--warning)" };
    case "expired":
      return { ...base, backgroundColor: "var(--surface-elevated)", color: "var(--text-muted)" };
    case "revoked":
      return { ...base, backgroundColor: "var(--danger-bg)", color: "var(--danger)" };
    default:
      return { ...base, backgroundColor: "var(--surface-elevated)", color: "var(--text-muted)" };
  }
}

const ORG_ID_REGEX = /^org_[a-zA-Z0-9]+$/;

export default function PortalInviteUsers() {
  const { permissions, seats, refresh, role, memberRole, tenant } = usePortalAuth();
  const effectiveRole = role || memberRole || "";

  const [emails, setEmails] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [showLinkModal, setShowLinkModal] = useState(false);
  const [orgId, setOrgId] = useState("");
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState(null);

  const [invitations, setInvitations] = useState([]);
  const [invitationStats, setInvitationStats] = useState(null);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [invitationFilter, setInvitationFilter] = useState("all");
  const [actioningInvitation, setActioningInvitation] = useState(null);
  const [localSeatUsage, setLocalSeatUsage] = useState(null);

  const isSuperAdmin = effectiveRole === "super_admin";
  const canManageUsers = isSuperAdmin || (Array.isArray(permissions) && permissions.includes("users.manage"));

  const seatLabel = useMemo(() => formatSeatLabel(localSeatUsage, seats), [localSeatUsage, seats]);
  const remainingSeats = useMemo(() => computeRemaining(localSeatUsage, seats), [localSeatUsage, seats]);
  const noSeatsAvailable = remainingSeats === 0;

  const canLinkOrg = ["super_admin", "owner", "admin"].includes(effectiveRole);
  const showUnlinkedBanner = !isTenantLinkedToClerk(tenant) && canLinkOrg;

  const loadInvitations = async (statusFilter = null) => {
    if (!canManageUsers) return;

    setLoadingInvitations(true);
    try {
      const params = statusFilter && statusFilter !== "all" ? { status: statusFilter } : {};
      const result = await getPortalInvitations(params);
      setInvitations(result.invitations || []);
      setInvitationStats(result.stats || null);
      setLocalSeatUsage(result.seatUsage || null); // ✅ NEW: Store seat usage from endpoint
    } catch (err) {
      console.error("Failed to load invitations:", err);
    } finally {
      setLoadingInvitations(false);
    }
  };

  useEffect(() => {
    if (canManageUsers) {
      loadInvitations(invitationFilter === "all" ? null : invitationFilter);
    }
  }, [canManageUsers, invitationFilter]);

  if (!canManageUsers) {
    return (
      <ErrorBoundary componentName="PortalInviteUsers">
        <div style={{ minHeight: "100vh", background: "var(--bg-secondary)", padding: "var(--space-2xl)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto" }}>
            <div
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-2xl)",
                textAlign: "center",
                border: "1px solid var(--border-primary)",
              }}>
              <h1 style={{ fontSize: "24px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-sm)" }}>Access Denied</h1>
              <p style={{ color: "var(--text-secondary)", margin: 0 }}>You do not have permission to manage users.</p>
            </div>
          </div>
        </div>
      </ErrorBoundary>
    );
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
    setSuccess(null);

    const entries = parseEmails(emails);
    if (!entries.length) {
      setError("Enter at least one email address.");
      return;
    }

    setSubmitting(true);
    try {
      const result = await createPortalInvitations(entries);
      const invited = Array.isArray(result?.invitations) ? result.invitations.length : 0;
      setSuccess(invited ? `Sent ${invited} invitation${invited === 1 ? "" : "s"}.` : "No invitations were sent.");
      setEmails("");
      await refresh().catch(() => {});
      await loadInvitations(invitationFilter === "all" ? null : invitationFilter);
    } catch (err) {
      if (err?.status === 409 && err?.data?.code === "SEAT_LIMIT") {
        const remaining = err?.data?.seatUsage?.remaining;
        if (remaining === 0) {
          setError("Seat limit reached. Remove an active member before inviting more users.");
        } else if (remaining != null) {
          setError(`Only ${remaining} seat${remaining === 1 ? "" : "s"} available.`);
        } else {
          setError("Seat limit reached. Please try again later.");
        }
      } else if (err?.status === 409 && (err?.data?.error === "organization_not_configured" || err?.data?.error === "organization_mismatch")) {
        setError(
          err?.data?.error === "organization_mismatch" ? "This workspace is linked to a different organization." : "This workspace isn't connected to a Clerk organization yet."
        );
      } else {
        setError(err?.message || "Unable to send invitations.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleLinkOrg = async () => {
    if (!tenant?.id) {
      setLinkError("No tenant context available.");
      return;
    }
    if (!ORG_ID_REGEX.test(orgId.trim())) {
      setLinkError("Enter a valid Clerk organization ID (e.g., org_abc123).");
      return;
    }
    setLinkBusy(true);
    setLinkError(null);
    try {
      await linkTenantOrganization(tenant.id, orgId.trim());
      await refresh().catch(() => {});
      setShowLinkModal(false);
      setOrgId("");
      setError(null);
    } catch (e) {
      setLinkError(e?.message || "Failed to link organization.");
    } finally {
      setLinkBusy(false);
    }
  };

  const handleResend = async (invitationId) => {
    setActioningInvitation(invitationId);
    try {
      await resendInvitation(invitationId);
      setSuccess("Invitation resent successfully.");
      await loadInvitations(invitationFilter === "all" ? null : invitationFilter);
    } catch (err) {
      setError(err?.message || "Failed to resend invitation.");
    } finally {
      setActioningInvitation(null);
    }
  };

  const handleRevoke = async (invitationId) => {
    if (!confirm("Are you sure you want to revoke this invitation?")) return;

    setActioningInvitation(invitationId);
    try {
      await revokeInvitation(invitationId);
      setSuccess("Invitation revoked successfully.");
      await loadInvitations(invitationFilter === "all" ? null : invitationFilter);
    } catch (err) {
      setError(err?.message || "Failed to revoke invitation.");
    } finally {
      setActioningInvitation(null);
    }
  };

  return (
    <ErrorBoundary componentName="PortalInviteUsers">
      <div style={{ minHeight: "100vh", background: "var(--bg-secondary)" }}>
        {/* Header */}
        <div style={{ background: "var(--accent-gradient)", borderBottom: "1px solid var(--border-secondary)" }}>
          <div style={{ maxWidth: "var(--container-max)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <h1 style={{ fontSize: "32px", fontWeight: "400", color: "#fff", margin: "0 0 var(--space-sm) 0", letterSpacing: "-0.5px" }}>Invite Users</h1>
                <p style={{ fontSize: "15px", color: "rgba(255,255,255,0.9)", margin: 0 }}>{seatLabel}</p>
              </div>

              {canLinkOrg && (
                <button
                  onClick={() => setShowLinkModal(true)}
                  style={{
                    padding: "var(--space-sm) var(--space-lg)",
                    backgroundColor: "rgba(255,255,255,0.2)",
                    color: "#fff",
                    border: "1px solid rgba(255,255,255,0.3)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                  }}>
                  Link Organization
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ maxWidth: "var(--container-max)", margin: "0 auto", padding: "var(--space-2xl) var(--space-xl)" }}>
          {showUnlinkedBanner && (
            <div
              style={{
                backgroundColor: "var(--warning-bg)",
                border: "1px solid var(--warning)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-2xl)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <div style={{ color: "var(--warning)" }}>This workspace isn't connected to a Clerk organization.</div>
              <button
                onClick={() => setShowLinkModal(true)}
                style={{
                  padding: "var(--space-sm) var(--space-lg)",
                  backgroundColor: "var(--warning)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "var(--radius-sm)",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}>
                Link Organization
              </button>
            </div>
          )}

          {noSeatsAvailable && (
            <div
              style={{
                backgroundColor: "var(--warning-bg)",
                border: "1px solid var(--warning)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-2xl)",
              }}>
              <p style={{ margin: 0, color: "var(--warning)" }}>You have reached your seat limit. Remove an active member or upgrade your plan to invite more users.</p>
            </div>
          )}

          {error && (
            <div
              style={{
                backgroundColor: "var(--danger-bg)",
                border: "1px solid var(--danger)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-xl)",
              }}>
              <p style={{ color: "var(--danger)", margin: 0, fontSize: "14px" }}>{error}</p>
            </div>
          )}

          {success && (
            <div
              style={{
                backgroundColor: "var(--success-bg)",
                border: "1px solid var(--success)",
                borderRadius: "var(--radius-md)",
                padding: "var(--space-lg)",
                marginBottom: "var(--space-xl)",
              }}>
              <p style={{ color: "var(--success)", margin: 0, fontSize: "14px", fontWeight: "500" }}>✓ {success}</p>
            </div>
          )}

          {/* Invite Form */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              padding: "var(--space-xl)",
              marginBottom: "var(--space-2xl)",
              boxShadow: "var(--shadow-sm)",
            }}>
            <form onSubmit={handleSubmit} style={{ display: "grid", gap: "var(--space-lg)" }}>
              <div>
                <label style={{ display: "block", fontSize: "14px", fontWeight: "500", color: "var(--text-primary)", marginBottom: "var(--space-xs)" }}>Email Addresses</label>
                <textarea
                  rows={4}
                  placeholder="user@example.com, another@example.com"
                  value={emails}
                  onChange={(e) => setEmails(e.target.value)}
                  disabled={submitting || noSeatsAvailable}
                  style={{
                    width: "100%",
                    padding: "var(--space-md)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-primary)",
                    fontFamily: "inherit",
                    resize: "vertical",
                  }}
                />
                <p style={{ fontSize: "13px", color: "var(--text-muted)", margin: "var(--space-xs) 0 0" }}>Separate multiple emails with commas or new lines.</p>
              </div>

              <button
                type="submit"
                disabled={submitting || noSeatsAvailable}
                style={{
                  padding: "var(--space-md) var(--space-xl)",
                  backgroundColor: submitting || noSeatsAvailable ? "var(--text-disabled)" : "var(--accent-primary)",
                  color: "#fff",
                  border: "none",
                  borderRadius: "var(--radius-md)",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: submitting || noSeatsAvailable ? "not-allowed" : "pointer",
                  width: "fit-content",
                }}>
                {submitting ? "Sending…" : "Send Invitations"}
              </button>
            </form>
          </div>

          {/* Invitation History */}
          <div
            style={{
              backgroundColor: "var(--surface)",
              borderRadius: "var(--radius-lg)",
              border: "1px solid var(--border-primary)",
              overflow: "hidden",
              boxShadow: "var(--shadow-sm)",
            }}>
            <div
              style={{
                padding: "var(--space-xl)",
                borderBottom: "1px solid var(--border-primary)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}>
              <h2 style={{ fontSize: "18px", fontWeight: "600", color: "var(--text-primary)", margin: 0 }}>Invitation History</h2>
              {invitationStats && (
                <div style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                  {invitationStats.pending} pending · {invitationStats.accepted} accepted · {invitationStats.expired} expired
                </div>
              )}
            </div>

            <div style={{ padding: "var(--space-lg)", borderBottom: "1px solid var(--border-primary)", display: "flex", gap: "var(--space-sm)" }}>
              {["all", "pending", "accepted", "expired", "revoked"].map((filter) => (
                <button
                  key={filter}
                  onClick={() => setInvitationFilter(filter)}
                  style={{
                    padding: "var(--space-sm) var(--space-md)",
                    backgroundColor: invitationFilter === filter ? "var(--accent-primary)" : "var(--surface-elevated)",
                    color: invitationFilter === filter ? "#fff" : "var(--text-primary)",
                    border: invitationFilter === filter ? "none" : "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                    fontWeight: "500",
                    cursor: "pointer",
                    textTransform: "capitalize",
                  }}>
                  {filter}
                </button>
              ))}
            </div>

            {loadingInvitations ? (
              <div style={{ padding: "var(--space-2xl)", textAlign: "center" }}>
                <p style={{ color: "var(--text-muted)" }}>Loading invitations...</p>
              </div>
            ) : invitations.length === 0 ? (
              <div style={{ padding: "var(--space-2xl)", textAlign: "center" }}>
                <p style={{ color: "var(--text-muted)", margin: 0 }}>No invitations found.</p>
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ backgroundColor: "var(--surface-elevated)", borderBottom: "1px solid var(--border-primary)" }}>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Email</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Role</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Status</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Invited By</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Invited</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Expires</th>
                      <th style={{ padding: "var(--space-md)", textAlign: "left", fontSize: "13px", fontWeight: "600", color: "var(--text-secondary)" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv) => (
                      <tr key={inv.id} style={{ borderBottom: "1px solid var(--border-primary)" }}>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)" }}>{inv.email}</td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-primary)", textTransform: "capitalize" }}>{inv.role}</td>
                        <td style={{ padding: "var(--space-md)" }}>
                          <span style={getStatusBadgeStyle(inv.status)}>{inv.status}</span>
                        </td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-muted)" }}>{inv.invitedBy?.name || inv.invitedBy?.email || "--"}</td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-muted)" }}>{formatDate(inv.invitedAt)}</td>
                        <td style={{ padding: "var(--space-md)", fontSize: "14px", color: "var(--text-muted)" }}>{formatDate(inv.expiresAt)}</td>
                        <td style={{ padding: "var(--space-md)" }}>
                          {inv.status === "pending" && (
                            <div style={{ display: "flex", gap: "var(--space-xs)" }}>
                              <button
                                onClick={() => handleResend(inv.id)}
                                disabled={actioningInvitation === inv.id}
                                title="Resend invitation"
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor: "var(--surface-elevated)",
                                  color: "var(--text-primary)",
                                  border: "1px solid var(--border-primary)",
                                  borderRadius: "var(--radius-sm)",
                                  fontSize: "12px",
                                  cursor: actioningInvitation === inv.id ? "not-allowed" : "pointer",
                                }}>
                                {actioningInvitation === inv.id ? "..." : "⟳"}
                              </button>
                              <button
                                onClick={() => handleRevoke(inv.id)}
                                disabled={actioningInvitation === inv.id}
                                title="Revoke invitation"
                                style={{
                                  padding: "4px 8px",
                                  backgroundColor: "var(--danger-bg)",
                                  color: "var(--danger)",
                                  border: "1px solid var(--danger)",
                                  borderRadius: "var(--radius-sm)",
                                  fontSize: "12px",
                                  cursor: actioningInvitation === inv.id ? "not-allowed" : "pointer",
                                }}>
                                ✕
                              </button>
                            </div>
                          )}
                          {inv.status === "expired" && (
                            <button
                              onClick={() => handleResend(inv.id)}
                              disabled={actioningInvitation === inv.id}
                              style={{
                                padding: "4px 12px",
                                backgroundColor: "var(--surface-elevated)",
                                color: "var(--text-primary)",
                                border: "1px solid var(--border-primary)",
                                borderRadius: "var(--radius-sm)",
                                fontSize: "12px",
                                cursor: actioningInvitation === inv.id ? "not-allowed" : "pointer",
                              }}>
                              {actioningInvitation === inv.id ? "..." : "Resend"}
                            </button>
                          )}
                          {(inv.status === "accepted" || inv.status === "revoked") && <span style={{ color: "var(--text-muted)", fontSize: "13px" }}>--</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Link Organization Modal */}
        {showLinkModal && (
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
            onClick={() => setShowLinkModal(false)}>
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                backgroundColor: "var(--surface)",
                borderRadius: "var(--radius-lg)",
                padding: "var(--space-2xl)",
                maxWidth: "500px",
                width: "90%",
                boxShadow: "var(--shadow-xl)",
              }}>
              <h3 style={{ fontSize: "20px", fontWeight: "600", color: "var(--text-primary)", marginBottom: "var(--space-lg)" }}>Link Clerk Organization</h3>
              <p style={{ fontSize: "14px", color: "var(--text-secondary)", marginBottom: "var(--space-lg)" }}>
                Enter the <code style={{ backgroundColor: "var(--surface-elevated)", padding: "2px 6px", borderRadius: "var(--radius-sm)" }}>org_…</code> ID that matches this
                workspace's slug.
              </p>
              <input
                type="text"
                value={orgId}
                onChange={(e) => setOrgId(e.target.value)}
                placeholder="org_123abcxyz"
                disabled={linkBusy}
                style={{
                  width: "100%",
                  padding: "var(--space-md)",
                  border: "1px solid var(--border-primary)",
                  borderRadius: "var(--radius-md)",
                  fontSize: "14px",
                  backgroundColor: "var(--surface)",
                  color: "var(--text-primary)",
                  marginBottom: "var(--space-md)",
                }}
              />
              {linkError && (
                <div
                  style={{
                    padding: "var(--space-md)",
                    backgroundColor: "var(--danger-bg)",
                    border: "1px solid var(--danger)",
                    borderRadius: "var(--radius-sm)",
                    marginBottom: "var(--space-lg)",
                  }}>
                  <p style={{ color: "var(--danger)", fontSize: "14px", margin: 0 }}>{linkError}</p>
                </div>
              )}
              <div style={{ display: "flex", gap: "var(--space-md)", justifyContent: "flex-end" }}>
                <button
                  onClick={() => setShowLinkModal(false)}
                  disabled={linkBusy}
                  style={{
                    padding: "var(--space-md) var(--space-xl)",
                    backgroundColor: "var(--surface)",
                    color: "var(--text-secondary)",
                    border: "1px solid var(--border-primary)",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    fontWeight: "500",
                    cursor: linkBusy ? "not-allowed" : "pointer",
                  }}>
                  Cancel
                </button>
                <button
                  onClick={handleLinkOrg}
                  disabled={linkBusy || !ORG_ID_REGEX.test(orgId.trim())}
                  style={{
                    padding: "var(--space-md) var(--space-xl)",
                    backgroundColor: linkBusy || !ORG_ID_REGEX.test(orgId.trim()) ? "var(--text-disabled)" : "var(--accent-primary)",
                    color: "#fff",
                    border: "none",
                    borderRadius: "var(--radius-md)",
                    fontSize: "14px",
                    fontWeight: "600",
                    cursor: linkBusy || !ORG_ID_REGEX.test(orgId.trim()) ? "not-allowed" : "pointer",
                  }}>
                  {linkBusy ? "Linking…" : "Link"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
}
